'use client';

import { useState, useEffect, useRef, type ReactNode } from 'react';
import { useLocale } from 'next-intl';

import { toast } from '@/components/storefront/ui/toast';
import { Button } from '@/components/storefront/ui/button';
import { IconButton } from '@/components/storefront/ui/icon-button';
import { ShoppingBag, Plus, Minus } from '@phosphor-icons/react';
import { useCart } from '@/lib/cart/CartContext';
import { availableUnits, CartStockError } from '@/lib/cart/stock';
import { maxWholeUnits, snapToWholeUnits, toBaseQty, toUnitQty } from '@/lib/units/convert';
import { capturePostHogEvent } from '@/lib/analytics/posthog-capture';
import type { CartAddon } from '@/lib/cart/addons';
import { addonsUnitTotal } from '@/lib/cart/addons';
import { useCurrency } from '@/lib/currency/CurrencyContext';
import { formatPrice } from '@/lib/currency/formatPrice';
import type { PriceTierDTO } from '@/lib/storefront/products/service';
import {
  applySaleRatio,
  matchTierForQty,
  resolveDisplayPricing,
  tiersForVariant,
} from '@/lib/storefront/products/tiers';

// Re-export the canonical type for consumers that previously imported it from
// this file (during the storefront-service refactor).
export type PriceTierDisplay = PriceTierDTO;

type AddToCartButtonProps = {
  productId: string;
  minQty: number;
  /** Increment/decrement step for the quantity stepper. Defaults to 1. */
  qtyStep?: number;
  price: number;
  variant?: 'full' | 'icon';
  variantId?: string | null;
  disabled?: boolean;
  /**
   * The shopper has not picked their options yet, as opposed to the product
   * being unavailable. Both block the add, but they are not the same message:
   * labelling an un-chosen variable product "out of stock" tells shoppers the
   * item cannot be bought and sends them off the page.
   */
  needsSelection?: boolean;
  /** Effective available units (null/undefined = untracked / unlimited). */
  stock?: number | null;
  hasPriceTiers?: boolean;
  priceTiers?: PriceTierDTO[];
  /** The viewer is in a price group, so a tier may only lower `price`. */
  inPriceGroup?: boolean;
  /**
   * Active campaign for this product/variant. Without it this block prices
   * tiers as if no discount existed — which is what made the PDP quote a
   * discounted headline and an undiscounted total on the same screen.
   */
  sale?: { sale_price: number; original_price: number } | null;
  /**
   * Optional sibling rendered next to the Add to Cart button as a 50/50 grid.
   * Used by the PDP to place a Wishlist button alongside.
   */
  extraAction?: ReactNode;
  /** Chosen add-ons; priced into the line total and stored on the cart line. */
  addons?: CartAddon[] | null;
  /** Called instead of adding when the selection is incomplete. */
  onInvalidAddons?: () => void;
  /** True when required add-on groups are unsatisfied. */
  addonsInvalid?: boolean;
  /** Notifies parent when quantity changes — for live price updates upstream. */
  onQtyChange?: (qty: number) => void;
  /** The `product_units` row id for the selected unit, if the product has units. */
  productUnitId?: string | null;
  /**
   * Base units per whole selected unit (1000 for a carton of 1000 pieces).
   * 1 — the default — means the product is sold in its base unit and every
   * quantity rule below collapses to the behaviour that predates units.
   */
  unitFactor?: number;
  /** Label of the selected unit, used to quote the ceiling in whole units. */
  unitLabel?: string | null;
};

export default function AddToCartButton({
  productId,
  minQty,
  qtyStep = 1,
  price,
  variant = 'full',
  variantId,
  disabled = false,
  needsSelection = false,
  stock,
  hasPriceTiers = false,
  priceTiers = [],
  inPriceGroup = false,
  sale = null,
  extraAction,
  addons,
  onInvalidAddons,
  addonsInvalid = false,
  onQtyChange,
  productUnitId,
  unitFactor = 1,
  unitLabel = null,
}: AddToCartButtonProps) {
  const step = qtyStep > 0 ? qtyStep : 1;
  // A selling unit can only be bought whole, so the ceiling is the largest
  // whole number of units the stock can fill — NOT the raw stock. With a
  // 1000-piece carton and 2500 in stock the ceiling is 2000: offering 2500
  // would store a quantity checkout permanently rejects
  // (`quantity % factor !== 0`) and the shopper cannot edit their way out of.
  // At factor 1 this is the untouched `availableUnits(stock)`.
  const factor = unitFactor > 1 ? Math.floor(unitFactor) : 1;
  const maxQty =
    factor > 1 ? toBaseQty(maxWholeUnits(stock, factor), factor) : availableUnits(stock);
  const locale = useLocale();
  const { addItem } = useCart();
  const { currency } = useCurrency();
  const [qty, setQtyRaw] = useState(minQty);
  // The parent (PDP) mirrors this quantity for its price block and its
  // "N cartons" line. Notifying from an effect rather than from inside the
  // state updater keeps the two in lockstep no matter which path moved qty —
  // typing, stepping, a variant switch or a unit switch — so the number shown
  // is always the number `handleAddToCart` sends.
  const onQtyChangeRef = useRef(onQtyChange);
  onQtyChangeRef.current = onQtyChange;
  useEffect(() => {
    onQtyChangeRef.current?.(qty);
  }, [qty]);
  // A useState initializer only runs on mount, so when this component stays
  // mounted across a variant or unit switch (PDP) qty never re-syncs on its
  // own. At factor 1 this stays raise-only: never lower qty when minQty falls,
  // the shopper may have deliberately chosen a larger quantity. On a unit line
  // it additionally re-snaps to a whole multiple of the NEW factor and drops
  // to the new whole-unit ceiling — a partial unit is unsellable, so leaving
  // one behind is not "keeping the shopper's choice", it is a dead end.
  useEffect(() => {
    setQtyRaw((q) => {
      if (factor <= 1) return q < minQty ? minQty : q;
      const snapped = snapToWholeUnits(q, factor, minQty);
      return maxQty >= minQty ? Math.min(snapped, maxQty) : snapped;
    });
  }, [minQty, factor, maxQty]);
  const setQty = (next: number | ((prev: number) => number)) => {
    setQtyRaw((prev) => (typeof next === 'function' ? next(prev) : next));
  };
  const [loading, setLoading] = useState(false);
  const atMax = qty >= maxQty;
  // Stock that cannot fill even one whole unit leaves nothing to buy in this
  // unit. Clamping qty to zero would be silent and unrecoverable, so the
  // selection stays put and the CTA falls back to the out-of-stock treatment
  // this component already uses. Never reachable at factor 1, where the
  // pre-existing over-minimum behaviour is preserved exactly.
  const unitUnavailable = factor > 1 && Number.isFinite(maxQty) && maxQty < minQty;
  const blocked = disabled || unitUnavailable;

  const isRTL = locale === 'ar';
  // Same rule as the price block above and the cart below: a matched tier sets
  // the unit price for this quantity, then the campaign discount compounds onto
  // it. With no tier matched the campaign's own numbers are exact.
  const matchedTier = hasPriceTiers
    ? matchTierForQty(priceTiers, qty, variantId)
    : null;
  const { displayPrice: effectivePrice } = resolveDisplayPricing({
    tierPrice: matchedTier?.unit_price ?? price,
    tierMatched: matchedTier !== null,
    sale,
    untieredPrice: inPriceGroup ? price : undefined,
  });

  // The tier table advertises what the customer would actually pay at each
  // break, so the campaign has to come off these too.
  const saleRatio =
    sale && sale.original_price > 0
      ? sale.sale_price / sale.original_price
      : null;
  // Every tier is listed whenever the product has any. A tier only ever lowers
  // a group price (a wholesale shopper's group price can already beat the
  // default tiers), so for them a row shows what they would actually pay.
  const untieredPrice = sale?.sale_price ?? price;
  const displayTiers = hasPriceTiers ? tiersForVariant(priceTiers, variantId) : [];
  const tierRowPrice = (unitPrice: number) => {
    const tiered = applySaleRatio(unitPrice, saleRatio);
    return inPriceGroup && untieredPrice > 0 ? Math.min(untieredPrice, tiered) : tiered;
  };
  // Add-ons are priced as themselves — tier and campaign discounts apply to the
  // product, which is how the cart and the server both compute the line.
  const unitWithAddons = effectivePrice + addonsUnitTotal(addons);

  async function handleAddToCart() {
    // Checked here rather than by disabling the button: a disabled CTA leaves
    // the shopper guessing which group they missed, so this surfaces the
    // unmet groups instead of silently doing nothing.
    if (addonsInvalid) {
      onInvalidAddons?.();
      return;
    }
    setLoading(true);
    try {
      const quantity = variant === 'icon' ? minQty : qty;
      await addItem(productId, quantity, minQty, variantId, stock, addons, productUnitId);
      capturePostHogEvent('add_to_cart', {
        product_id: productId,
        price: effectivePrice,
        quantity,
        variant_id: variantId,
      });
      toast.success(isRTL ? 'تمت الإضافة للسلة' : 'Added to cart');
    } catch (err) {
      if (err instanceof CartStockError) {
        toast.danger(
          isRTL
            ? `الكمية المتاحة في المخزون ${err.available} فقط`
            : `Only ${err.available} in stock`,
        );
      } else {
        toast.danger(
          isRTL
            ? 'تعذّر إضافة المنتج. حاول مرة أخرى.'
            : 'Could not add product. Please try again.',
        );
      }
    } finally {
      setLoading(false);
    }
  }

  // Icon-only variant for product cards
  if (variant === 'icon') {
    return (
      <IconButton
        type="button"
        onClick={handleAddToCart}
        disabled={disabled}
        loading={loading}
        size="lg"
        shape="rounded"
        color="primary"
        variant="solid"
        aria-label={isRTL ? 'إضافة إلى السلة' : 'Add to Cart'}
        icon={<ShoppingBag />}
      />
    );
  }

  // Full variant: tier table + quantity stepper + CTA row (with optional sibling).
  // "Choose options" before "out of stock": a variable product with nothing
  // selected yet is not unavailable, it is unconfigured. Saying otherwise is
  // what made shoppers leave the page before they had picked anything.
  const ctaLabel = needsSelection
    ? isRTL ? 'تحديد الخيارات' : 'Select Options'
    : blocked
      ? isRTL ? 'غير متوفر' : 'Out of Stock'
      : isRTL ? 'إضافة إلى السلة' : 'Add to Cart';

  const decreaseQty = () => setQty((q) => Math.max(minQty, q - step));
  const increaseQty = () => setQty((q) => Math.min(maxQty, q + step));
  // Typed entry is the one path with no step built in. Snap DOWN onto the
  // whole-unit grid before clamping, so 1234 against a 1000-piece carton
  // becomes 1000 rather than a quantity checkout rejects. At factor 1 this is
  // the same `max(minQty, val)` then `min(maxQty, …)` as before.
  const commitTypedQty = (val: number) =>
    setQty(Math.min(maxQty, snapToWholeUnits(val, factor, minQty)));

  // Quoted in whole units when a unit is selected, so this can never read
  // "Max available: 2500" directly above the PDP's "2 cartons in stock".
  const maxLabel =
    factor > 1 && unitLabel
      ? `${toUnitQty(maxQty, factor)} ${unitLabel}`
      : `${maxQty}`;

  return (
    <div className="space-y-4">
      {displayTiers.length > 0 && (
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-subtle text-muted-foreground">
                <th className="px-3 py-2 text-start font-medium">
                  {isRTL ? 'الكمية' : 'Quantity'}
                </th>
                <th className="px-3 py-2 text-start font-medium">
                  {isRTL ? 'سعر الوحدة' : 'Unit Price'}
                </th>
              </tr>
            </thead>
            <tbody>
              {displayTiers.map((tier, i) => {
                const isActive = qty >= tier.min_qty && (tier.max_qty === null || qty <= tier.max_qty);
                return (
                  <tr key={i} className={isActive ? 'bg-primary/5 font-medium' : ''}>
                    <td className="px-3 py-2 border-t border-border">
                      {tier.max_qty !== null
                        ? `${tier.min_qty.toLocaleString()} – ${tier.max_qty.toLocaleString()}`
                        : `${tier.min_qty.toLocaleString()}+`}
                    </td>
                    <td className="px-3 py-2 border-t border-border">
                      {formatPrice(tierRowPrice(tier.unit_price), currency)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Quantity stepper */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-center rounded-[var(--radius-field)] border border-border overflow-hidden">
          <button
            type="button"
            onClick={decreaseQty}
            disabled={qty <= minQty || blocked}
            aria-label={isRTL ? 'تقليل الكمية' : 'Decrease quantity'}
            className="h-11 w-11 flex items-center justify-center text-foreground hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Minus size={16} />
          </button>
          {hasPriceTiers ? (
            <input
              type="number"
              value={qty}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val)) commitTypedQty(val);
              }}
              min={minQty}
              max={Number.isFinite(maxQty) ? maxQty : undefined}
              step={step}
              className="h-11 w-[64px] border-x border-border bg-background text-center text-sm font-medium tabular-nums focus:outline-none focus:ring-2 focus:ring-ring [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          ) : (
            <span className="h-11 min-w-[48px] flex items-center justify-center px-2 border-x border-border text-sm font-medium tabular-nums">
              {qty}
            </span>
          )}
          <button
            type="button"
            onClick={increaseQty}
            disabled={blocked || atMax}
            aria-label={isRTL ? 'زيادة الكمية' : 'Increase quantity'}
            className="h-11 w-11 flex items-center justify-center text-foreground hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Plus size={16} />
          </button>
        </div>

        {!blocked && Number.isFinite(maxQty) && qty >= maxQty && (
          <span className="text-xs text-muted-foreground">
            {isRTL ? `الحد الأقصى المتاح: ${maxLabel}` : `Max available: ${maxLabel}`}
          </span>
        )}

        {/* Line total. This used to be tier-only, which meant the one case
            where the maths is trivial (flat price × qty) was the case the
            shopper had to do in their head. It tracks the stepper, and on a
            tiered product `effectivePrice` is already the tier's unit price,
            so the same line covers both. */}
        {unitWithAddons > 0 && (
          <div className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground tabular-nums">
              {formatPrice(unitWithAddons * qty, currency)}
            </span>{' '}
            <span className="tabular-nums">
              ({formatPrice(unitWithAddons, currency)} × {qty.toLocaleString()})
            </span>
          </div>
        )}
      </div>

      {/* CTA row — single button or 2-col grid when extraAction is present */}
      {extraAction ? (
        <div className="grid grid-cols-2 gap-3">
          <Button
            type="button"
            onClick={handleAddToCart}
            disabled={blocked}
            loading={loading}
            color="primary"
            variant="solid"
            size="lg"
            fullWidth
            leftIcon={<ShoppingBag />}
          >
            {ctaLabel}
          </Button>
          {extraAction}
        </div>
      ) : (
        <Button
          type="button"
          onClick={handleAddToCart}
          disabled={blocked}
          loading={loading}
          color="primary"
          variant="solid"
          size="lg"
          fullWidth
          leftIcon={<ShoppingBag />}
        >
          {ctaLabel}
        </Button>
      )}
    </div>
  );
}
